// src/Components/Luna/lunaPowerStore.ts
// Luna power-ups, the live half: a plain module (like Ui/rewardFlightStore)
// that every activity feeds with the server's `luna` payload.
//
//   const token = lunaBurst(fromEl);   // lesson: stardust leaves at once
//   reportLuna(res.data.luna, { token });
//   reportLuna(res.data.luna);         // graded runners: burst from the last tap
//
// One burst per correct answer: stardust particles fly from where the learner
// tapped into the Luna ring in the top bar (data-luna-ring), and the ring
// fills when they land. The star meter flies its own star in the same tick,
// so it reads as one moment. Level-ups pulse the ring; an evolution is queued
// for LunaEvolution, which waits while an activity holds celebrations (the
// lesson holds them until its results card shows). Results screens read the
// session (beginLunaSession at the start of an activity).
//
// No ring on screen or prefers-reduced-motion: no particles, the ring updates
// at once.

import {
  addToSession,
  burstCount,
  LunaEvolvedTo,
  LunaPayload,
  LunaSession,
  LunaView,
  nextCombo,
  startSession,
  viewFromPayload,
} from "./lunaModel";
import { soundManager } from "../../utils/SoundManager";

export interface Point {
  x: number;
  y: number;
}

export interface LunaFlight {
  id: number;
  from: Point;
  to: Point;
  count: number;
  boosted: boolean;
}

export interface LunaGain {
  id: number;
  amount: number;
  boosted: boolean;
  capped: boolean;
}

export interface LunaCelebration extends LunaEvolvedTo {
  id: number;
  creatureId: string;
}

export interface LunaStoreState {
  /** The truth from the server (or seeded from the inventory). */
  view: LunaView | null;
  /** What the ring shows: lags `view` until the stardust lands. */
  shown: LunaView | null;
  gain: LunaGain | null;
  /** Bumps on every level-up the ring should pulse for. */
  levelPulse: number;
  /** Bumps when the Luna becomes charged (flare). */
  chargePulse: number;
  flights: LunaFlight[];
  celebrations: LunaCelebration[];
  holds: number;
  session: LunaSession | null;
}

let state: LunaStoreState = {
  view: null,
  shown: null,
  gain: null,
  levelPulse: 0,
  chargePulse: 0,
  flights: [],
  celebrations: [],
  holds: 0,
  session: null,
};

const listeners = new Set<() => void>();
let seq = 1;

function emit() {
  listeners.forEach((l) => l());
}
function set(patch: Partial<LunaStoreState>) {
  state = { ...state, ...patch };
  emit();
}

export function subscribeLuna(l: () => void): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}
export function getLunaState(): LunaStoreState {
  return state;
}

/* ───────────── environment ───────────── */

export const FLIGHT_MS = 720;
const STAGGER_MS = 34;

function reducedMotion(): boolean {
  try {
    return typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

let lastPointer: { at: number; p: Point } | null = null;
if (typeof window !== "undefined") {
  window.addEventListener(
    "pointerdown",
    (e) => {
      lastPointer = { at: Date.now(), p: { x: e.clientX, y: e.clientY } };
    },
    { capture: true, passive: true },
  );
}

/** The visible ring the stardust should fly into, if any. */
export function ringTarget(): Point | null {
  if (typeof document === "undefined") return null;
  const els = Array.from(document.querySelectorAll<HTMLElement>("[data-luna-ring]"));
  for (let i = els.length - 1; i >= 0; i--) {
    const r = els[i].getBoundingClientRect();
    if (r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < window.innerHeight) {
      return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
    }
  }
  return null;
}

function originOf(from?: Element | Point | null): Point {
  if (from && typeof (from as Element).getBoundingClientRect === "function") {
    const r = (from as Element).getBoundingClientRect();
    if (r.width || r.height) return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  } else if (from && typeof (from as Point).x === "number") {
    return from as Point;
  }
  if (lastPointer && Date.now() - lastPointer.at < 4000) return lastPointer.p;
  const w = typeof window !== "undefined" ? window.innerWidth : 390;
  const h = typeof window !== "undefined" ? window.innerHeight : 844;
  return { x: w / 2, y: h * 0.62 };
}

/* ───────────── seeding ───────────── */

/** Show this Luna when nothing (or another Luna) is showing. No animation. */
export function seedLuna(view: LunaView | null) {
  if (!view) return;
  const cur = state.view;
  if (cur && cur.creatureId === view.creatureId && cur.xp >= view.xp) return;
  set({ view, shown: view });
}

/* ───────────── bursts ───────────── */

interface Pending {
  landAt: number;
  payload: LunaPayload | null;
  committed: boolean;
}
const pending = new Map<number, Pending>();

function launch(from: Point, count: number, boosted: boolean): number | null {
  const to = ringTarget();
  if (!to || reducedMotion()) return null;
  const id = seq++;
  set({ flights: [...state.flights, { id, from, to, count, boosted }] });
  window.setTimeout(() => {
    set({ flights: state.flights.filter((f) => f.id !== id) });
  }, FLIGHT_MS + count * STAGGER_MS + 80);
  return id;
}

/**
 * Lesson answers: send stardust now, before the server confirms, so the star
 * and the stardust leave together. Returns a token for reportLuna.
 */
export function lunaBurst(from?: Element | Point | null): number {
  const token = seq++;
  const combo = nextCombo(state.shown?.combo, true);
  const flight = launch(originOf(from), burstCount(10, combo.boosted), combo.boosted);
  const landAt = Date.now() + (flight ? FLIGHT_MS : 0);
  pending.set(token, { landAt, payload: null, committed: false });
  // A request that never answers must not leak.
  window.setTimeout(() => pending.delete(token), 15000);
  // Optimistic charge pip so the ring reacts with the burst.
  if (state.shown) {
    const shownCombo = { ...combo };
    window.setTimeout(() => {
      const p = pending.get(token);
      if (p && !p.committed && state.shown) set({ shown: { ...state.shown, combo: shownCombo } });
    }, Math.max(0, landAt - Date.now()));
  }
  window.setTimeout(() => tryCommit(token), Math.max(0, landAt - Date.now()));
  return token;
}

/** A miss: the charge goes, nothing flies. */
export function lunaMiss() {
  if (state.shown) set({ shown: { ...state.shown, combo: nextCombo(state.shown.combo, false) } });
}

function tryCommit(token: number) {
  const p = pending.get(token);
  if (!p || p.committed || !p.payload) return;
  if (Date.now() < p.landAt) return;
  p.committed = true;
  pending.delete(token);
  commit(p.payload);
}

function commit(payload: LunaPayload) {
  const prevShown = state.shown;
  const next = viewFromPayload(prevShown && prevShown.creatureId === payload.creatureId ? prevShown : state.view, payload);
  if (!next) return;
  const gainAmount = Math.max(0, payload.xpGained || 0);
  const patch: Partial<LunaStoreState> = { shown: next };
  if (gainAmount > 0 || payload.capped) {
    patch.gain = { id: seq++, amount: gainAmount, boosted: !!payload.combo?.boosted, capped: !!payload.capped };
  }
  if (payload.leveledUp) patch.levelPulse = state.levelPulse + 1;
  const becameCharged = !!next.combo.charged && !(prevShown?.combo?.charged && prevShown.creatureId === next.creatureId);
  if (becameCharged) patch.chargePulse = state.chargePulse + 1;
  set(patch);
  if (gainAmount > 0) soundManager.play(payload.combo?.boosted ? "combo_boost" : "stardust");
  if (becameCharged) soundManager.play("combo_charge");
  if (payload.leveledUp) soundManager.play("level_up");
}

/**
 * Feed a server `luna` payload in. With a token from lunaBurst the ring fills
 * when that burst lands; without one a burst is launched now (from `from`, or
 * the learner's last tap) when stardust was earned.
 */
export function reportLuna(payload: LunaPayload | null | undefined, opts: { token?: number; from?: Element | Point | null } = {}) {
  if (!payload) return;
  if (!payload.creatureId) {
    if (state.shown) set({ shown: { ...state.shown, combo: { ...state.shown.combo, ...(payload.combo || {}) } as any } });
    return;
  }

  const view = viewFromPayload(state.view && state.view.creatureId === payload.creatureId ? state.view : null, payload);
  const session = state.session ? addToSession(state.session, payload, view) : null;
  const celebrations =
    payload.evolved && payload.evolvedTo
      ? [...state.celebrations, { ...payload.evolvedTo, id: seq++, creatureId: payload.creatureId }]
      : state.celebrations;
  set({ view, session, celebrations });

  // Let the rest of the app (inventory, Lunas page) catch up.
  try {
    window.dispatchEvent(new CustomEvent(LUNA_EVENT, { detail: payload }));
  } catch {
    /* non-browser */
  }

  if (opts.token != null && pending.has(opts.token)) {
    const p = pending.get(opts.token)!;
    p.payload = payload;
    window.setTimeout(() => tryCommit(opts.token!), Math.max(0, p.landAt - Date.now()));
    return;
  }

  const gained = Math.max(0, payload.xpGained || 0);
  const flight = gained > 0 ? launch(originOf(opts.from), burstCount(gained, !!payload.combo?.boosted), !!payload.combo?.boosted) : null;
  if (flight) window.setTimeout(() => commit(payload), FLIGHT_MS);
  else commit(payload);
}

export const LUNA_EVENT = "moonrock:luna";

/* ───────────── sessions (results cards) ───────────── */

/** Start counting what this activity gives the Luna. */
export function beginLunaSession() {
  set({ session: startSession(state.view) });
}
export function getLunaSession(): LunaSession | null {
  return state.session;
}

/* ───────────── evolution celebrations ───────────── */

/** Keep evolution celebrations queued (an activity is running). Returns the release. */
export function holdLunaCelebrations(): () => void {
  set({ holds: state.holds + 1 });
  let released = false;
  return () => {
    if (released) return;
    released = true;
    set({ holds: Math.max(0, state.holds - 1) });
  };
}

export function currentCelebration(s: LunaStoreState = state): LunaCelebration | null {
  return s.holds === 0 && s.celebrations.length ? s.celebrations[0] : null;
}

export function dismissCelebration(id: number) {
  set({ celebrations: state.celebrations.filter((c) => c.id !== id) });
}

/** Test helper: back to an empty store. */
export function __resetLunaStore() {
  pending.clear();
  state = { view: null, shown: null, gain: null, levelPulse: 0, chargePulse: 0, flights: [], celebrations: [], holds: 0, session: null };
  emit();
}
